import { DragDropContext, Draggable, Droppable, type DropResult } from "@hello-pangea/dnd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";

interface CustomerRow {
  id: string;
  label: string;
  provider_id: string;
}

interface SettingsRow {
  id: string;
  provider_priority_order: string[];
}

// TODO: drop the UntypedSupabase facade once `pnpm --filter qa-app run sb:fetch-db-types`
// has been re-run against a Supabase project where the review_queue_settings migration
// (classifier/drizzle/0297_luxuriant_rattler.sql) is applied. Once `Database` in
// `@/integrations/supabase/types` includes `review_queue_settings`, the facade and the
// two `untyped as ...` casts can be removed.
type UntypedResult = { data: unknown; error: { message: string } | null };
interface UntypedSelectChain {
  eq: (col: string, val: string) => UntypedSelectChain;
  maybeSingle: () => Promise<UntypedResult>;
}
interface UntypedUpdateChain {
  eq: (col: string, val: string) => Promise<UntypedResult>;
}
interface UntypedQuery {
  select: (cols: string) => UntypedSelectChain;
  update: (patch: Record<string, unknown>) => UntypedUpdateChain;
}
interface UntypedSupabase {
  from: (table: string) => UntypedQuery;
}
const untyped = supabase as unknown as UntypedSupabase;

export default function ReviewQueuePrioritySection() {
  const queryClient = useQueryClient();
  const [order, setOrder] = useState<string[]>([]);

  const { data: customers } = useQuery({
    queryKey: ["customers", "for-priority"],
    queryFn: async () => {
      const { data, error } = await supabase.from("customers").select("id,label,provider_id").order("label");
      if (error) throw error;
      return (data ?? []) as CustomerRow[];
    },
  });

  const { data: settings } = useQuery({
    queryKey: ["review_queue_settings"],
    queryFn: async () => {
      const { data, error } = await untyped
        .from("review_queue_settings")
        .select("id,provider_priority_order")
        .eq("id", "default")
        .maybeSingle();
      if (error) throw error;
      return (data ?? { id: "default", provider_priority_order: [] }) as SettingsRow;
    },
    refetchOnWindowFocus: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  useEffect(() => {
    if (!settings) return;
    const next = settings.provider_priority_order ?? [];
    setOrder((prev) => (prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next));
  }, [settings]);

  const customersByProvider = useMemo(() => {
    const map = new Map<string, CustomerRow[]>();
    for (const c of customers ?? []) {
      const existing = map.get(c.provider_id) ?? [];
      existing.push(c);
      map.set(c.provider_id, existing);
    }
    return map;
  }, [customers]);

  const knownProviders = useMemo(
    () => Array.from(new Set((customers ?? []).map((c) => c.provider_id))).sort(),
    [customers],
  );

  const danglingCount = order.filter((p) => !customersByProvider.has(p)).length;
  const availableToAdd = knownProviders.filter((p) => !order.includes(p));

  const saveMutation = useMutation({
    mutationFn: async (next: string[]) => {
      const { error } = await untyped
        .from("review_queue_settings")
        .update({ provider_priority_order: next, updated_at: new Date().toISOString() })
        .eq("id", "default");
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["review_queue_settings"] });
      toast.success("Review queue priority saved");
    },
    onError: (error: Error) => toast.error(`Failed to save: ${error.message}`),
  });

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const next = [...order];
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setOrder(next);
  };

  return (
    <div className="space-y-4 border rounded-lg p-6 bg-card">
      <h2 className="text-xl font-semibold">Review Queue Priority</h2>
      <p className="text-sm text-muted-foreground">
        Reviewers see encounters from listed providers first (in this order). An empty list means the oldest-discharged
        encounter wins, regardless of provider.
      </p>

      {danglingCount > 0 && (
        <p className="text-sm text-destructive">
          {danglingCount} unknown provider{danglingCount === 1 ? "" : "s"} in the saved list. Save to drop.
        </p>
      )}

      {order.length === 0 ? (
        <p className="text-sm text-muted-foreground">No preference set.</p>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="priority">
            {(provided) => (
              <ul ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                {order.map((providerId, idx) => {
                  const matchingCustomers = customersByProvider.get(providerId) ?? [];
                  const labels = matchingCustomers.map((c) => c.label).join(", ");
                  return (
                    <Draggable key={providerId} draggableId={providerId} index={idx}>
                      {(p) => (
                        <li
                          ref={p.innerRef}
                          {...p.draggableProps}
                          data-testid="priority-item"
                          className="flex items-center justify-between border rounded px-3 py-2 bg-background"
                        >
                          <div
                            {...p.dragHandleProps}
                            className="flex-1 cursor-grab select-none"
                            aria-label={`Drag handle for ${providerId}`}
                          >
                            <span className="font-medium">{providerId}</span>
                            {labels && <span className="ml-2 text-sm text-muted-foreground">{labels}</span>}
                            {matchingCustomers.length === 0 && (
                              <span className="ml-2 text-sm text-destructive">(no customers)</span>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setOrder(order.filter((x) => x !== providerId))}
                          >
                            Remove
                          </Button>
                        </li>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <div className="flex items-center gap-2">
        {availableToAdd.length > 0 && (
          <Select value="" onValueChange={(value) => setOrder([...order, value])}>
            <SelectTrigger aria-label="Add provider" className="w-64">
              <SelectValue placeholder="Add provider..." />
            </SelectTrigger>
            <SelectContent>
              {availableToAdd.map((p) => {
                const customerLabels = (customersByProvider.get(p) ?? []).map((c) => c.label).join(", ");
                return (
                  <SelectItem key={p} value={p}>
                    {p}
                    {customerLabels && ` — ${customerLabels}`}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}
        <Button
          onClick={() => saveMutation.mutate(order.filter((p) => customersByProvider.has(p)))}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? "Saving priority..." : "Save priority"}
        </Button>
      </div>
    </div>
  );
}
